import { Outlet, Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, LayoutDashboard, Server, Package, Radio, BarChart3, ChevronDown } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'

interface AppInfo {
  app_id: string
  name: string
  status: string
}

export default function AppLayout() {
  const { appId } = useParams<{ appId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [app, setApp] = useState<AppInfo | null>(null)
  const [apps, setApps] = useState<AppInfo[]>([])
  const [showAppSelector, setShowAppSelector] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadApps()
    if (appId) {
      loadApp(appId)
    }
  }, [appId])

  // 点击其他地方关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowAppSelector(false)
      }
    }

    if (showAppSelector) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showAppSelector])

  const loadApps = async () => {
    try {
      const response = await fetch('/api/admin/apps')
      const data = await response.json()
      setApps(data.apps || [])
    } catch (error) {
      console.error('加载应用列表失败:', error)
    }
  }

  const loadApp = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/apps/${id}`)
      const data = await response.json()
      setApp(data)
    } catch (error) {
      console.error('加载应用信息失败:', error)
    }
  }

  const navItems = [
    { path: `/apps/${appId}`, label: '概览', icon: LayoutDashboard },
    { path: `/apps/${appId}/devices`, label: '设备管理', icon: Server },
    { path: `/apps/${appId}/versions`, label: '版本管理', icon: Package },
    { path: `/apps/${appId}/gray-release`, label: '灰度发布', icon: Radio },
    { path: `/apps/${appId}/stats`, label: '统计分析', icon: BarChart3 }
  ]

  const handleAppSelect = (selectedAppId: string) => {
    navigate(`/apps/${selectedAppId}`)
    setShowAppSelector(false)
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* 侧边栏 */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-dark-card border-r border-dark-border">
        {/* 返回按钮 */}
        <div className="p-6 border-b border-dark-border">
          <Link
            to="/apps"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">返回应用列表</span>
          </Link>
        </div>

        {/* 应用选择器 */}
        <div className="p-4 border-b border-dark-border relative" ref={selectorRef}>
          <button
            onClick={() => setShowAppSelector(!showAppSelector)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-dark-border rounded-lg hover:bg-primary-600/20 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded bg-primary-600 flex items-center justify-center flex-shrink-0">
                <Package size={16} className="text-white" />
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {app?.name || '加载中...'}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {appId || ''}
                </p>
              </div>
            </div>
            <ChevronDown size={16} className="text-gray-400" />
          </button>

          {/* 下拉菜单 */}
          {showAppSelector && (
            <div className="absolute left-4 right-4 mt-2 w-auto bg-dark-card border border-dark-border rounded-lg shadow-xl z-50">
              <div className="py-2 max-h-64 overflow-y-auto">
                {apps.map((a) => (
                  <button
                    key={a.app_id}
                    onClick={() => handleAppSelect(a.app_id)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-dark-border transition-colors ${
                      a.app_id === appId ? 'text-primary-400 bg-primary-600/10' : 'text-gray-300'
                    }`}
                  >
                    <p className="font-medium truncate">{a.name}</p>
                    <p className="text-xs text-gray-500 truncate">{a.app_id}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 导航菜单 */}
        <nav className="mt-4">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-600/10 text-primary-400 border-r-2 border-primary-500'
                    : 'text-gray-400 hover:text-white hover:bg-dark-border'
                }`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            )
          })}
        </nav>
      </aside>

      {/* 主内容区 */}
      <main className="ml-64">
        <header className="h-16 bg-dark-card border-b border-dark-border flex items-center justify-between px-6">
          <h2 className="text-lg font-semibold text-white">
            {navItems.find(item => item.path === location.pathname)?.label || app?.name}
          </h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              {new Date().toLocaleDateString('zh-CN')}
            </span>
          </div>
        </header>

        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
